import { tokens } from '@neo4j-ndl/base';
import { IconButtonArray, useNeedleTheme } from '@neo4j-ndl/react';
import type NVL from '@neo4j-nvl/base';
import type { NvlOptions } from '@neo4j-nvl/base';
import { InteractiveNvlWrapper, type InteractiveNvlWrapperProps } from '@neo4j-nvl/react';
import { DEFAULT_RELATIONSHIP_COLOR } from '@nx/state';
import { nanoid } from '@reduxjs/toolkit';
import { default as cx } from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import {
  BoxSelectButton,
  DownloadButton,
  LassoSelectButton,
  SingleSelectButton,
  ToggleSidePanelButton,
  ZoomInButton,
  ZoomOutButton,
  ZoomToFitButton,
} from './graph-viz-buttons';
import {
  type Gesture,
  type GraphSelection,
  GraphVizContext,
  type InteractionMode,
  type NeoNode,
  type NeoRel,
  type NvlGraph,
  type Sidepanel,
} from './graph-viz-context';
import { GraphVizSidepanel } from './graph-viz-sidepanel';
import { mapToNvlGraph } from './map-to-nvl-graph';
import { SingleSelectionSidepanelContents } from './sidepanel-components/single-selection-sidepanel-contents';
import { useManagedSelectionState } from './use-managed-selection-state';
import { useSemicontrolledState } from './use-semi-controlled-state';

type Placement = 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';

const PLACEMENTS: Record<Placement, string> = {
  'top-right': 'top-2 right-2',
  'top-left': 'top-2 left-2',
  'bottom-right': 'bottom-2 right-2',
  'bottom-left': 'bottom-2 left-2',
};

function InteractionIsland({
  children,
  className,
  placement,
}: {
  children: React.ReactNode;
  placement: Placement;
  className?: string;
}) {
  return <div className={cx('absolute', PLACEMENTS[placement], className)}>{children}</div>;
}

export const DEFAULT_NVL_OPTIONS: NvlOptions = {
  renderer: 'canvas',
  disableWebGL: true,
  minZoom: 0.05,
  maxZoom: 3,
  relationshipThreshold: 0.55,
  disableTelemetry: true,
};

const DEFAULT_COMPONENTS = {
  topRightIsland: (
    <div className="flex gap-2">
      <DownloadButton />
      <ToggleSidePanelButton />
    </div>
  ),
  topLeftIsland: null,
  bottomLeftIsland: null,
  bottomRightIsland: (
    <IconButtonArray orientation="vertical" isFloating>
      <ZoomInButton />
      <ZoomOutButton />
      <ZoomToFitButton />
    </IconButtonArray>
  ),
};

export type GraphVizProps = {
  nvlRef?: React.RefObject<NVL>;
  sidepanel?: Sidepanel | null;
  topRightIsland?: React.ReactNode;
  bottomLeftIsland?: React.ReactNode;
  bottomRightIsland?: React.ReactNode;
  topLeftIsland?: React.ReactNode;
  className?: string;

  gesture?: Gesture;
  setGesture?: (gesture: Gesture) => void;
  selected?: GraphSelection;
  setSelected?: (newSelection: GraphSelection) => void;
  interactionMode?: InteractionMode;
  setInteractionMode?: (interactionMode: InteractionMode) => void;

  nodes: NeoNode[];
  rels: NeoRel[];
} & InteractiveNvlWrapperProps;

export const GraphViz = ({
  nvlRef: rawNvlRef,
  nvlCallbacks,
  nvlOptions,
  sidepanel: rawSidepanel,
  nodes: rawNodes,
  rels: rawRels,
  topLeftIsland = DEFAULT_COMPONENTS.topLeftIsland,
  topRightIsland = DEFAULT_COMPONENTS.topRightIsland,
  bottomLeftIsland = DEFAULT_COMPONENTS.bottomLeftIsland,
  bottomRightIsland = DEFAULT_COMPONENTS.bottomRightIsland,
  gesture = 'single',
  setGesture,
  selected: rawSelected,
  setSelected: rawSetSelected,
  interactionMode: rawInteractionMode,
  setInteractionMode: rawSetInteractionMode,
  mouseEventCallbacks = {},
  className,
  ...restProps
}: GraphVizProps) => {
  const nvlRef = useMemo(() => rawNvlRef ?? React.createRef<NVL>(), [rawNvlRef]);

  const instanceId = useMemo(() => nanoid(), []);

  // Respect NDL theme changes
  const { theme } = useNeedleTheme();
  const { bg, border } = tokens.theme[theme].palette.neutral;

  // key required for nvl react responsiveness
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [theme]);

  // Semi-control interaction mode and selection state
  const [interactionMode, setInteractionMode] = useSemicontrolledState(
    rawInteractionMode ?? 'select',
    rawSetInteractionMode,
  );
  const [selected, setSelected] = useSemicontrolledState(
    rawSelected ?? { nodeIds: [], relationshipIds: [] },
    rawSetSelected,
  );

  const nvlGraph = useMemo((): NvlGraph => mapToNvlGraph(rawNodes, rawRels), [rawNodes, rawRels]);

  const { nodesWithSelection, relsWithSelection, wrappedMouseEventCallbacks } = useManagedSelectionState({
    gesture,
    interactionMode,
    setInteractionMode,
    mouseEventCallbacks,
    selected,
    setSelected,
    nvlGraph,
  });

  const [isSidePanelOpen, setIsSidePanelOpen] = useSemicontrolledState(
    rawSidepanel?.isSidePanelOpen ?? true,
    rawSidepanel?.setIsSidePanelOpen,
  );
  const [sidePanelWidth, setSidePanelWidth] = useSemicontrolledState(
    rawSidepanel?.sidePanelWidth ?? 400,
    rawSidepanel?.onSidePanelResize,
  );

  const sidepanel = useMemo((): Sidepanel | null => {
    if (rawSidepanel === undefined) {
      return {
        contents: <GraphViz.SidepanelContents />,
        isSidePanelOpen,
        setIsSidePanelOpen,
        sidePanelWidth,
        onSidePanelResize: setSidePanelWidth,
      };
    }
    return rawSidepanel;
  }, [rawSidepanel, isSidePanelOpen, setIsSidePanelOpen, sidePanelWidth, setSidePanelWidth]);

  return (
    <GraphVizContext.Provider
      value={{
        nvlInstance: nvlRef.current,
        sidepanel,
        gesture,
        setGesture,
        interactionMode,
        selected,
        nvlGraph,
      }}
    >
      <div className={cx('bg-palette-neutral-bg-default relative w-full min-w-[25px]', className)}>
        <InteractiveNvlWrapper
          key={key}
          layout="d3Force"
          nodes={nodesWithSelection}
          rels={relsWithSelection}
          nvlOptions={{
            ...DEFAULT_NVL_OPTIONS,
            instanceId,
            styling: {
              selectedInnerBorderColor: bg.default,
              defaultRelationshipColor: DEFAULT_RELATIONSHIP_COLOR,
              dropShadowColor: border.weak,
            },
            ...nvlOptions,
          }}
          nvlCallbacks={{
            onLayoutComputing(isComputing) {
              if (!isComputing) {
                nvlRef.current?.fit(
                  nvlRef.current.getNodes().map((neighbors) => neighbors.id),
                  { noPan: true },
                );
              }
            },
            ...nvlCallbacks,
          }}
          mouseEventCallbacks={wrappedMouseEventCallbacks}
          ref={nvlRef}
          {...restProps}
        />
        {topLeftIsland !== null && <InteractionIsland placement="top-left">{topLeftIsland}</InteractionIsland>}

        {topRightIsland !== null && <InteractionIsland placement="top-right">{topRightIsland}</InteractionIsland>}

        {bottomLeftIsland !== null && <InteractionIsland placement="bottom-left">{bottomLeftIsland}</InteractionIsland>}

        {bottomRightIsland !== null && (
          <InteractionIsland placement="bottom-right">{bottomRightIsland}</InteractionIsland>
        )}
      </div>
      {sidepanel && <GraphVizSidepanel sidepanel={sidepanel} />}
    </GraphVizContext.Provider>
  );
};

GraphViz.ZoomInButton = ZoomInButton;
GraphViz.ZoomOutButton = ZoomOutButton;
GraphViz.ZoomToFitButton = ZoomToFitButton;
GraphViz.ToggleSidePanelButton = ToggleSidePanelButton;
GraphViz.DownloadButton = DownloadButton;
GraphViz.BoxSelectButton = BoxSelectButton;
GraphViz.LassoSelectButton = LassoSelectButton;
GraphViz.SingleSelectButton = SingleSelectButton;
GraphViz.SidepanelContents = SingleSelectionSidepanelContents;
